<template>
  <div class="admission-note-container">
    <page-header
      :actions="pageHeaderActionsConfig"
      left-side-class="col-md-6"
      :page="pageHeaderConfig"
      right-side-class="col-md-6  align-items-end d-flex pb-2 justify-content-end"
    />
    <admission-note-document
      v-if="
        admissionNoteInfo && admissionInfo.studentServiceId && !isSignHubActive
      "
      :admission-id="admissionId"
      :admission-note-id="admissionNoteInfo.id"
      :student-service-id="admissionInfo.studentServiceId"
    />
    <board :details="invoiceDetailsBoard" />
    <validation-observer ref="admissionNote">
      <b-tabs
        v-if="isSignHubActive"
        v-model="tabModel"
        class="mt-3"
        nav-class="custom-nav-container"
        :no-nav-style="true"
      >
        <b-tab :active="true" title="Nota de Admisión">
          <!-- Secciones -->
          <div v-for="(section, idx) in sections" :key="idx">
            <admission-note-section
              :ref="`section-${section.sectionType}`"
              :read-only="readOnly"
              :section-data="section"
            />
          </div>

          <!-- Firma del especialista (aunque el hub esté activo) -->
          <validation-provider
            v-slot="{ validate: _validate, errors }"
            :rules="{ required: { allowFalse: false } }"
          >
            <custom-signature
              activation-button-text="Presione aquí para añadir firma del especialista"
              allow-add-more
              :apply-signature="false"
              cannot-edit-name
              class="mt-4"
              icon-title="fas fa-file-user"
              keep-signed
              :limit-new-signatures-to="1"
              :requesters="requesterModel"
              :specialist-data="{
                specialists: specialists,
                showSpecialistField: true,
              }"
              :submitted="readOnly"
              title="Firma de Participantes"
              title-class="section-container__section-title__info"
              title-container-class="section-container__section-title__container"
              use-role
              :user-profile="user.profile"
              @input="_validate"
            />
            <error
              v-if="errors[0] && hasSignatureValidationError"
              message="La Firma de Participante es requerida."
              variant="no-border"
            />
          </validation-provider>
        </b-tab>

        <b-tab v-if="canViewAdmissionSignTab" title="Firmas">
          <div class="my-4">
            <sign-plan
              :key="admissionNoteInfo.id"
              ref="signPlanRef"
              :disabled="isElectronicDisabled"
              :is-electronic-signature.sync="isElectronicSignature"
              question-label="¿Cuál será el mecanismo que se estará utilizando para firmar la Nota de Admisión?"
              @change="handleSelectMechanism"
            />
            <div class="mt-4">
              <div v-if="hasSelectMechanisManuscript">
                <section-title content="Documento" has-line />
                <div class="bg-secondary-13 rounded p-3">
                  <admission-note-document
                    ref="admissionNoteDocument"
                    :admission-id="admissionId"
                    :admission-note-id="admissionNoteInfo.id"
                    :student-service-id="admissionInfo.studentServiceId"
                    @documentSaved="handleAdmissionNoteSaved"
                  />
                </div>
              </div>
              <signature-hub-info
                v-if="hasSelectMechanismElectronic"
                :signature-info="signatureHubInfo"
                @configure-sign="handleConfigureSignatures"
              />
            </div>
          </div>
        </b-tab>
      </b-tabs>

      <div v-else>
        <div v-for="(section, idx) in sections" :key="idx">
          <admission-note-section
            :ref="`section-${section.sectionType}`"
            :read-only="readOnly"
            :section-data="section"
          />
        </div>
        <validation-provider
          v-slot="{ validate: _validate, errors }"
          :rules="{ required: { allowFalse: false } }"
        >
          <custom-signature
            activation-button-text="Presione aquí para añadir firma del especialista"
            allow-add-more
            :apply-signature="false"
            cannot-edit-name
            class="mt-4"
            icon-title="fas fa-file-user"
            keep-signed
            :limit-new-signatures-to="1"
            :requesters="requesterModel"
            :specialist-data="{
              specialists: specialists,
              showSpecialistField: true,
            }"
            :submitted="readOnly"
            title="Firma de Participantes"
            title-class="section-container__section-title__info"
            title-container-class="section-container__section-title__container"
            use-role
            :user-profile="user.profile"
            @input="_validate"
          />
          <error
            v-if="errors[0] && hasSignatureValidationError"
            message="La Firma de Participante es requerida."
            variant="no-border"
          />
        </validation-provider>
      </div>
    </validation-observer>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';
import sysparameterByNames from '@/utils/constants/sysParameterNames';
import AdmissionNoteSection from '@/views/provider/referral-management/search/components/AdmissionNoteSection.vue';
import AdmissionNoteDocument from './components/AdmissionNoteDocument.vue';
import alertMixin from '@/mixins/alert.mixin';
import {
  getStudentReferralAdmision,
  getSingleAdmissionNote,
  getSpecialistsForAssignment,
  addAdmissionNote,
  getAdmissionNote,
  saveAdmissionNoteMechanism,
  deleteDocumentByDocumentId,
  canCoordinateService as _canCoordinateService,
} from '@/modules/shared/studentreferral.api';
import CustomSignature from 'custom-signature';
import { name as userModuleName } from '@/modules/user/store';
import { mapState } from 'vuex';
import Error from 'custom-error';
import _generalApi from '@/modules/app/api';
import moment from 'moment';
import SectionTitle from 'SectionTitle';
import { SignPlan } from './components/sign-plan';
import signatureHubTypes from '@/utils/constants/signatureHubTypes.js';
import {
  getSignatureHubInfo as _getSignatureHubInfo,
  removeSignatureHub as _removeSignatureHub,
} from '@/modules/signatureHub/api';
import { SignatureHubInfo } from '@quisit/signature-hub';
import ReferralTypes from '@/utils/shared/constants/referralTypes';
export default {
  name: 'AdmissionNote',
  components: {
    PageHeader,
    Board,
    AdmissionNoteSection,
    AdmissionNoteDocument,
    CustomSignature,
    SectionTitle,
    SignPlan,
    SignatureHubInfo,
    Error,
  },
  mixins: [alertMixin],
  data: () => ({
    isElectronicDisabled: false,
    sysParameters: [],
    tabModel: null,
    selectedMechanismSignPlan: null,
    signatureHubInfo: null,
    admissionInfo: {},
    admissionNoteInfo: {
      id: null,
      findings: [{}],
      recommendationDate: '',
      recommendations: [{}],
    },
    requesterModel: [],
    specialists: [],
    isAmending: false,
    hasSignatureValidationError: false,
    from: 'referral-search',
    fromData: {
      reload: true,
    },
    routeName: '',
  }),
  computed: {
    ...mapState(userModuleName, ['user']),
    sections() {
      return [
        {
          sectionType: 'general',
          title: 'Información General',
          dateLabel: 'Fecha de Admisión:',
          textAreaLabel: 'Diagnóstico:',
          disabledDatePicker: true,
          textErrorMsg: 'El diagnóstico es requerido.',
          data: {
            date: this.admissionInfo.admissionDate,
            comments: [this.admissionNoteInfo.diagnostic],
          },
        },
        {
          sectionType: 'reasons',
          title: 'Parte I. Motivo de la Intervención',
          dateLabel: 'Fecha de Evaluación:',
          textAreaLabel: 'Razón de Admisión:',
          customTitle: true,
          dateErrorMsg: 'La fecha de evaluación es requerida.',
          textErrorMsg: 'La razón de admisión es requerida.',
          data: {
            comments: [this.admissionNoteInfo.reason],
            date:
              this.admissionNoteInfo.evaluationDate ||
              this.admissionInfo.evaluationDate,
          },
        },
        {
          sectionType: 'findings',
          title: 'Parte II. Hallazgos',
          dateLabel: 'Fecha:',
          textAreaLabel:
            'Observaciones en el área de especialidad hechas al momento de ver por primera vez al estudiante:',
          customTitle: true,
          multipleTextArea: true,
          subSectionTitle:
            'Resuma brevemente con fechas correspondientes, los servicios previos en su área, que se hayan provisto anteriormente al estudiante:',
          dateErrorMsg: 'La fecha es requerida',
          textErrorMsg:
            'Las observaciones en el área de especialidad son requeridas.',
          subErrorMsg: 'El servicio provisto es requerido.',
          data: this.readOnly
            ? {
                comments: this.admissionNoteInfo?.findings.map(
                  (f) => f.description
                ),
                date: this.admissionNoteInfo.findings[0].date,
                subComments: this.admissionNoteInfo.findingsSummary,
              }
            : null,
        },
        {
          sectionType: 'recommendations',
          title: 'Parte III. Recomendaciones',
          dateLabel: 'Fecha de Comienzo:',
          textAreaLabel: 'Comentario:',
          hasHourInput: true,
          customTitle: true,
          multipleTextArea: true,
          dateErrorMsg: 'La fecha de comienzo es requerida.',
          hourErrorMsg: 'La hora es requerida.',
          textErrorMsg: 'El comentario es requerido.',
          data: this.readOnly
            ? {
                comments: this.admissionNoteInfo.recommendations.map(
                  (r) => r.description
                ),
                date: this.admissionNoteInfo.recommendationDate.split('T')[0],
                hour: this.admissionNoteInfo.recommendationDate.split('T')[1],
              }
            : null,
        },
      ];
    },
    admissionId() {
      return Number(this.$route.params.admissionId);
    },
    noteId() {
      return Number(this.$route.params.noteId);
    },
    canModify() {
      return this.userHasPermissions('provider.amendant.admission.note');
    },
    isHistory() {
      return this.$route.params.isFromHistory;
    },
    isLastNote() {
      return this.$route.params.isLast;
    },
    readOnly() {
      return (
        this.admissionNoteInfo.id != null &&
        this.admissionNoteInfo != '' &&
        !this.isAmending
      );
    },
    invoiceDetailsBoard() {
      return [
        [
          {
            label: 'Estudiante:',
            icon: 'fas fa-address-book',
            data: `${this.admissionInfo.studentSieId} - ${this.admissionInfo.fullName}`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Edad:',
            icon: 'fas fa-calendar-edit',
            data: `${this.admissionInfo.studentAge} años`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Grado:',
            icon: 'fas fa-calendar-edit',
            data: this.admissionInfo.grade,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: this.admissionInfo.impediment,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Región:',
            icon: 'fas fa-map-marker-alt',
            data: this.admissionInfo.region,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Municipio:',
            icon: 'fas fa-map-marker-alt',
            data: this.admissionInfo.municipality,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Escuela:',
            icon: 'fas fa-graduation-cap',
            data: this.admissionInfo.place,
            cols: 'col-12 col-md-3',
          },
        ],
        [
          {
            label: 'Tipo de Servicio:',
            data: this.admissionInfo.disciplineName,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Modalidad:',
            data: this.admissionInfo.modalityTypeName,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Duración:',
            data: this.admissionInfo.duration,
            cols: 'col-4 col-sm col-12 mt-3',
          },
          {
            label: 'Frecuencia:',
            data: this.admissionInfo.frequency,
            cols: 'col-4 col-sm col-12 mt-3',
          },
        ],
      ];
    },
    pageHeaderConfig() {
      return {
        icon: 'fas fa-user-tie',
        title: 'Nota de Admisión',
        routes: ['Proveedor', 'Manejo de Referido', 'Nota de Admisión'],
      };
    },
    pageHeaderActionsConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.push({ name: 'referral-search' });
          },
        },
        {
          text: 'Coordinar Servicios',
          icon: 'fas fa-calendar',
          variant: 'success',
          visible:
            !this.hasCoordinatedService &&
            (this.readOnly || !this.isInitialReferralType),
          click: () => {
            this.goToServiceCoordination();
          },
        },
        {
          text: 'Enmendar Nota de admisión',
          icon: 'fas fa-files-medical',
          variant: 'success',
          visible:
            this.canModify &&
            this.admissionNoteInfo.isAmended != null &&
            this.isLastNote &&
            this.isHistory,
          click: this.amendNote,
        },
        {
          text: 'Historial de Enmienda',
          icon: 'fas fa-history',
          variant: 'outline-success',
          visible: this.admissionNoteInfo.isAmended != null,
          click: this.historyAmendNote,
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag-alt',
          variant: 'success',
          visible: !this.readOnly,
          click: this.submit,
        },
        {
          text: 'Imprimir',
          icon: 'fas fa-print',
          variant: 'outline-success',
          visible: this.readOnly && !this.hasSelectMechanismElectronic,
          click: this.printAdmissionNote,
        },
      ];
    },
    isSignHubActive() {
      return (
        this.getSysParemeterStaticValue(sysparameterByNames.ActiveSignHub) ===
        'true'
      );
    },
    canViewAdmissionSignTab() {
      return (
        this.isSignHubActive && this.admissionNoteInfo?.id && !this.isAmending
      );
    },
    hasSelectMechanisManuscript() {
      return this.selectedMechanismSignPlan === false;
    },
    hasSelectMechanismElectronic() {
      return this.selectedMechanismSignPlan === true;
    },
    isElectronicSignature() {
      return this.selectedMechanismSignPlan !== null
        ? this.selectedMechanismSignPlan
        : null;
    },
    isInitialReferralType() {
      return (
        this.admissionInfo.admissionCreationReasonId == ReferralTypes.Initial
      );
    },
    hasCoordinatedService() {
      return this.admissionInfo.hasCoordinateService;
    },
  },
  watch: {
    tabModel(newTabIndex) {
      if (
        newTabIndex === 1 &&
        this.$refs.admissionNoteDocument?.hasDocument?.() &&
        this.selectedMechanismSignPlan != null
      ) {
        this.selectedMechanismSignPlan = false; // manuscrito
        this.isElectronicDisabled = true; // bloquear selección
        this.$nextTick(() => {
          this.$refs.signPlanRef?.setSelectedQuestion(false);
        });
      }
    },
  },
  async mounted() {
    const { data: activeSignHubParameter } = await _getSysparameterByNames({
      parameterNames: [sysparameterByNames.ActiveSignHub],
    });
    this.sysParameters = [...activeSignHubParameter];
  },
  async created() {
    await this.getAdmissionNote();
    await this.getSpecialists();
  },
  methods: {
    getSysParemeterStaticValue(parameterName) {
      return (this.sysParameters.find((x) => x.name === parameterName) || {})
        .staticValue;
    },
    async getSignatureHubInfo() {
      try {
        const { data } = await _getSignatureHubInfo(
          signatureHubTypes.ADMISSION_NOTE,
          this.admissionId
        );
        this.signatureHubInfo = data;
        this.isElectronicDisabled =
          (this.signatureHubInfo?.documentInfo?.documentStatus == 'Firmado' ||
            this.signatureHubInfo?.documentInfo?.documentStatus ==
              'Pendiente de Firma') &&
          this.selectedMechanismSignPlan != null;
      } catch (error) {
        this.ShowToast(
          'Error',
          'No se pudo obtener la información de firma.',
          'error'
        );
      }
    },
    async handleAdmissionNoteSaved() {
      await this.getAdmissionInfo();
      this.isElectronicDisabled = true;
    },
    async handleSelectMechanism(selected) {
      const { data } = await getStudentReferralAdmision(this.admissionId);
      this.admissionInfo = data;

      const value = selected?.value;
      const previousValue = this.selectedMechanismSignPlan;
      const manuscriptHasDocument = this.$refs.admissionNoteDocument?.hasDocument();

      const isChangingElectronicToManuscript =
        value === false && previousValue === true;

      const isChangingManuscriptToElectronic =
        value === true && previousValue === false;

      if (isChangingManuscriptToElectronic && manuscriptHasDocument) {
        const confirmed = await this.ShowCancelModal(
          'Confirmación',
          'Usted ya se encuentra en un proceso de configuración de firma Manuscrito. ¿Está seguro que desea cambiar la configuración a Electronica?',
          'Sí',
          'No'
        );

        if (confirmed) {
          const deleted = await deleteDocumentByDocumentId(
            this.admissionInfo.referralDocs?.documentId,
            Number(this.admissionInfo.referralDocs?.relatedRecordId)
          ).catch((error) => {
            this.ShowToast(
              '',
              error.response?.data?.message ||
                'Error al eliminar el documento.',
              'error'
            );
          });

          if (deleted) {
            this.updateAdmissionNoteMechanism(value, previousValue);
            this.$refs.admissionNoteDocument.data = null;
          }
        } else {
          this.$nextTick(() => {
            this.selectedMechanismSignPlan = previousValue;
            this.$refs.signPlanRef?.setSelectedQuestion(previousValue);
          });
          return;
        }
      }

      if (
        isChangingElectronicToManuscript &&
        this.signatureHubInfo?.signatureHubId
      ) {
        const confirmChangeToManuscript = await this.ShowCancelModal(
          'Confirmación',
          'Usted ya se encuentra en un proceso de configuración de firma digital. ¿Está seguro que desea cambiar la configuración a manuscrito?',
          'Sí',
          'No'
        );

        if (confirmChangeToManuscript) {
          const response = await _removeSignatureHub(
            signatureHubTypes.ADMISSION_NOTE,
            this.admissionId
          ).catch((error) => {
            this.ShowToast('', error.response.data.message, 'error');
          });

          if (response?.data) {
            this.updateAdmissionNoteMechanism(value, previousValue);
            await this.getSignatureHubInfo();
          }
        } else {
          this.$nextTick(() => {
            this.selectedMechanismSignPlan = previousValue;
            this.$refs.signPlanRef?.setSelectedQuestion(previousValue);
          });
          return;
        }
      } else {
        // 🟢 Otros cambios válidos como null ➝ true o false ➝ true, etc.
        this.updateAdmissionNoteMechanism(value, previousValue);
      }
    },
    async updateAdmissionNoteMechanism(value, previousValue) {
      if (value !== undefined && value !== null && value != previousValue) {
        await saveAdmissionNoteMechanism(
          this.admissionInfo.admissionId,
          value
        ).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
        this.selectedMechanismSignPlan = value;
      }
    },
    handleConfigureSignatures() {
      this.$router.push({
        name: 'SignatureHub',
        params: {
          typeId: signatureHubTypes.ADMISSION_NOTE,
          relatedId: this.admissionInfo.admissionId,
          from: 'admissionNote',
          fromData: {
            admissionId: this.admissionInfo.admissionId,
            tabModel: this.tabModel,
          },
        },
      });
    },
    async getSpecialists() {
      const params = {
        locationFiscalYearId: 0,
        specialistId: 0,
        studentSieId: 0,
        isAdmission: false,
      };
      const { data } = await getSpecialistsForAssignment(
        this.admissionId,
        params.locationFiscalYearId,
        params.studentSieId,
        params.specialistId,
        params.isAdmission
      );
      this.specialists = data;
    },
    async amendNote() {
      if (this.isSignHubActive) {
        let hasStatus = ['Pendiente de Firma'].includes(
          this.signatureHubInfo?.documentInfo?.documentStatus
        );

        if (hasStatus) {
          this.ShowToast(
            'Advertencia',
            'Este documento se encuentra en proceso de firma, para proceder con el cambio se requiere Cancelar Proceso Firma dentro de la pantalla de configuración.',
            'warning'
          );
          return;
        }
      }
      this.isAmending = true;
      this.requesterModel = [];
    },
    async getAdmissionNote() {
      const { data } = this.isHistory
        ? await getSingleAdmissionNote(this.noteId)
        : await getAdmissionNote(this.admissionId);
      this.admissionNoteInfo = data;
      await this.getAdmissionInfo();

      if (
        this.admissionNoteInfo.isElectronicSignature === null &&
        this.admissionInfo?.referralDocs?.documentId
      ) {
        this.selectedMechanismSignPlan = false;
      } else {
        this.selectedMechanismSignPlan = this.admissionNoteInfo.isElectronicSignature;
      }

      await this.getSignatureHubInfo();

      if (this.readOnly) {
        this.requesterModel.push({
          id: this.admissionNoteInfo.signatureUser.id,
          name: this.admissionNoteInfo.signatureUser.name,
          signed: true,
          email: this.admissionNoteInfo.signatureEmail,
          phone: this.admissionNoteInfo.signaturePhoneNumber,
          specialistFullName: this.admissionNoteInfo
            .signatureSpecialistFullName,
          role: {
            id: this.admissionNoteInfo.signatureRole.id,
            name: this.admissionNoteInfo.signatureRole.name,
          },
          createdDate: moment(this.admissionNoteInfo.recommendationDate).format(
            'DD/MMM/YYYY'
          ),
        });
      }
    },
    async getAdmissionInfo() {
      const { data } = await getStudentReferralAdmision(this.admissionId);
      this.admissionInfo = { ...data };
      if (
        this.$refs.admissionNoteDocument?.hasDocument() &&
        this.admissionInfo.isElectronicSignature == false &&
        this.isSignHubActive
      ) {
        this.isElectronicDisabled = true;
      }
    },
    async addAdmissionNote(params) {
      await addAdmissionNote(this.admissionId, params)
        .then((res) => {
          this.ShowToast(
            'Proceso Exitoso',
            'La nota de admisión ha sido sometida exitosamente.',
            'success'
          );
          this.admissionNoteInfo = res.data;
        })
        .catch((err) => {
          if (err.response != null)
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error',
              5000
            );
        });
    },
    async submit() {
      const isValid = await this.$refs.admissionNote.validate();
      this.hasSignatureValidationError = !isValid;

      if (!isValid) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
        return;
      }
      if (this.canViewAdmissionSignTab) {
        const isValidSignPlan = this.$refs.signPlanRef?.validate?.();
        if (!isValidSignPlan) {
          this.ShowToast(
            'Alerta',
            'Debe completar la configuración de firma.',
            'error'
          );
          return;
        }
      }
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea someter la nota de admisión del estudiante <b>${this.admissionInfo.fullName}</b>?`,
        'Si',
        'No'
      );
      if (!answer) return;

      let notes = {};
      this.sections.forEach((s) => {
        const values = this.$refs[`section-${s.sectionType}`][0].getValues();
        notes[s.sectionType] = values;
      });

      const params = {
        draftingDate: notes.general.date,
        diagnostic: notes.general.comments[0],
        evaluationDate: new Date(notes.reasons.date),
        reason: notes.reasons.comments[0],
        findingsSummary: notes.findings.subComments,
        recommendationDate: new Date(
          new Date(notes.recommendations.date)
            .toISOString()
            .split('T')[0]
            .concat(`T${notes.recommendations.hour}`)
        ),
        findings: notes.findings.comments
          .filter((e) => e != '')
          .map((comment) => ({
            date: notes.findings.date,
            description: comment,
          })),
        recommendations: notes.recommendations.comments
          .filter((e) => e != '')
          .map((comment) => ({
            description: comment,
          })),
        signatureEmail: this.requesterModel[0].email,
        signaturePhoneNumber: this.requesterModel[
          this.requesterModel.length - 1
        ].phone,
        signatureSpecialistId: this.requesterModel[
          this.requesterModel.length - 1
        ].specialist?.id,
        signatureUserId: this.user.profile.sub,
        signatureRoleId: this.user.profile.roleid,
        isElectronicSignature: null,
      };

      await this.addAdmissionNote(params);

      if (this.admissionInfo.referralDocs && this.isSignHubActive) {
        await deleteDocumentByDocumentId(
          this.admissionInfo.referralDocs?.documentId,
          Number(this.admissionInfo.referralDocs?.relatedRecordId)
        ).catch((error) => {
          this.ShowToast(
            '',
            error.response?.data?.message || 'Error al eliminar el documento.',
            'error'
          );
        });
        if (this.$refs.admissionNoteDocument?.clearDocument) {
          this.$refs.admissionNoteDocument.clearDocument();
        }
        this.selectedMechanismSignPlan = null;
        this.isElectronicDisabled = false;
        this.admissionInfo.referralDocs = null;
      }

      this.isAmending = false;
    },
    async goToServiceCoordination() {
      const { data } = await _canCoordinateService(this.admissionId);

      if (!data.isValid) {
        this.ShowToast('Alerta', data.errorMessage, 'error');
        return;
      }

      //Validates document
      const isSignedAdmissionNote =
        this.signatureHubInfo.documentInfo?.documentStatusId === 5; //firmado

      const hasRefferralDoc = this.admissionInfo.referralDocs != null; //Nota de Admisión Firmada

      if (
        this.isInitialReferralType &&
        !isSignedAdmissionNote &&
        !hasRefferralDoc
      ) {
        this.ShowToast(
          'Alerta',
          'Se debe subir nota de admisión firmada para poder coordinar servicio.',
          'error'
        );
        return;
      }

      this.$router.push({
        name: 'SpecialistAvailabilityCalendarCoordination',
        params: { admissionId: this.admissionId, isCoordination: false },
      });
    },
    historyAmendNote() {
      this.$router.push({
        name: 'amendedNoteHistory',
        params: {
          admissionId: this.admissionId,
        },
      });
    },
    async printAdmissionNote() {
      let report = `referral/admissionnotereport/${this.admissionId}`;
      await _generalApi.downloadReport(report);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .custom-nav-container {
  border-bottom: 1px solid #e1e1e1;
  .nav-item .nav-link {
    font-size: 17.6px;
    font-weight: 700;
    &.active {
      border-bottom: 3px solid #2db782;
      color: #2db782;
    }
  }
}

.admission-note-container ::v-deep {
  .section-container {
    &__section-title {
      &__info {
        margin-top: 0;
      }
      &__container {
        background: #f5eee9;
        display: inline-flex;
        align-items: center;

        .title-icon {
          font-size: 0.75rem;
        }
        .dot {
          width: 1.75rem;
          height: 1.75rem;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }
    }
  }
}
</style>
